import React, { forwardRef, useState, useEffect, type HTMLAttributes } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";
import { Logo } from "./Logo";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

export interface SidebarMenuItem {
  id: string;
  label?: string;
  icon?: React.ComponentType<{ className?: string }>;
  href?: string;
  badge?: string;
  children?: SidebarMenuItem[];
  divider?: boolean;
}

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  menuItems: SidebarMenuItem[];
  activeItemId?: string;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  footerUser?: { name: string; email: string };
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

/** Safely extract initials from a name string. Handles empty, single, and accented names. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Recursively check if an item or any of its descendants is the active item. */
function isItemOrChildActive(item: SidebarMenuItem, activeItemId?: string): boolean {
  if (!activeItemId || item.divider) return false;
  if (item.id === activeItemId) return true;
  if (item.children) {
    return item.children.some((child) => isItemOrChildActive(child, activeItemId));
  }
  return false;
}

/** Shared base classes for section-header styling (button when expanded, Link when collapsed). */
const HEADER_BASE_CLASS =
  "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 border-l-[3px] text-sm font-medium text-white/70 transition-all duration-150 hover:bg-white/10 hover:text-white";

const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      className,
      menuItems,
      activeItemId,
      collapsed: controlledCollapsed,
      onCollapse,
      footerUser,
      mobileOpen,
      onMobileClose,
      ...props
    },
    ref,
  ) => {
    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

    // Track which sections are expanded
    const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
      const initial = new Set<string>();
      if (activeItemId) {
        for (const item of menuItems) {
          if (item.children && isItemOrChildActive(item, activeItemId)) {
            initial.add(item.id);
          }
        }
      }
      return initial;
    });

    // Auto-expand sections when activeItemId changes
    useEffect(() => {
      if (!activeItemId) return;
      setExpandedSections((prev) => {
        let needsUpdate = false;
        const next = new Set(prev);
        for (const item of menuItems) {
          if (item.children && isItemOrChildActive(item, activeItemId)) {
            if (!next.has(item.id)) {
              next.add(item.id);
              needsUpdate = true;
            }
          }
        }
        return needsUpdate ? next : prev;
      });
    }, [activeItemId, menuItems]);

    const toggleSection = (id: string) => {
      if (isCollapsed) return; // No-op when collapsed
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    };

    const handleToggle = () => {
      const next = !isCollapsed;
      if (onCollapse) {
        onCollapse(next);
      } else {
        setInternalCollapsed(next);
      }
    };

    const showBackdrop = !!mobileOpen;

    /** Recursively render a menu item and its children. */
    const renderItem = (item: SidebarMenuItem, depth: number = 0): React.ReactNode => {
      if (item.divider) {
        return (
          <li key={item.id} aria-hidden="true">
            <hr className="my-1 border-white/10" />
          </li>
        );
      }

      const Icon = item.icon;
      const isActive = item.id === activeItemId;
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedSections.has(item.id);
      // Collapsed groups render as a single Link: the "you are here" cue is
      // child-aware — it fires when the group OR any descendant is active,
      // and never when nothing inside matches (isItemOrChildActive semantics).
      const isSelfOrChildActive = isItemOrChildActive(item, activeItemId);

      if (hasChildren) {
        // ── Section header (collapsible group) ──
        const headerClassName = cn(
          HEADER_BASE_CLASS,
          isActive ? "bg-white/10 border-accent text-white" : "border-transparent",
          isCollapsed && "justify-center px-2",
        );
        const headerIcon = Icon ? <Icon className="h-5 w-5 flex-shrink-0" /> : <span className="h-5 w-5 flex-shrink-0" />;
        const headerCollapsedContent = isCollapsed && item.badge && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[8px] font-bold text-white">
            {item.badge}
          </span>
        );
        const headerExpandedContent = !isCollapsed && (
          <>
            <span className="flex-1 truncate text-left">{item.label}</span>
            {item.badge && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-accent px-1.5 text-[10px] font-semibold text-white">
                {item.badge}
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 flex-shrink-0 transition-transform duration-200",
                isExpanded && "rotate-180",
              )}
            />
          </>
        );

        // When collapsed, render as a link to the group's href so the page remains reachable.
        const headerContent = (
          <>
            {headerIcon}
            {headerExpandedContent}
            {headerCollapsedContent}
          </>
        );

        return (
          <li key={item.id}>
            {isCollapsed && item.href ? (
              <Link
                to={item.href}
                aria-current={isSelfOrChildActive ? "page" : undefined}
                className={cn(
                  HEADER_BASE_CLASS,
                  isSelfOrChildActive ? "bg-white/10 border-accent text-white" : "border-transparent",
                  "justify-center px-2",
                )}
                title={isCollapsed ? item.label : undefined}
              >
                {headerContent}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => toggleSection(item.id)}
                className={headerClassName}
                aria-expanded={!isCollapsed ? isExpanded : undefined}
                title={isCollapsed ? item.label : undefined}
              >
                {headerContent}
              </button>
            )}
            {/* Children (only visible when expanded and not collapsed) */}
            {!isCollapsed && isExpanded && item.children && (
              <ul className="flex flex-col gap-1 mt-1 ml-2 overflow-hidden transition-all duration-200">
                {item.children.map((child) => renderItem(child, depth + 1))}
              </ul>
            )}
          </li>
        );
      }

      // ── Regular item (leaf link) ──
      if (!item.href) return null;
      return (
        <li key={item.id}>
          <Link
            to={item.href}
            className={cn(
              "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 border-l-[3px] text-sm font-medium text-white/70 transition-all duration-150 hover:bg-white/10 hover:text-white",
              isActive ? "bg-white/10 border-accent text-white" : "border-transparent",
              isCollapsed && "justify-center px-2",
              depth > 0 && "ml-4",
            )}
            aria-current={isActive ? "page" : undefined}
            title={isCollapsed ? item.label : undefined}
          >
            {Icon ? <Icon className="h-5 w-5 flex-shrink-0" /> : <span className="h-5 w-5 flex-shrink-0" />}
            {!isCollapsed && (
              <>
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && (
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-accent px-1.5 text-[10px] font-semibold text-white">
                    {item.badge}
                  </span>
                )}
              </>
            )}
            {isCollapsed && item.badge && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[8px] font-bold text-white">
                {item.badge}
              </span>
            )}
          </Link>
        </li>
      );
    };

    return (
      <>
        {showBackdrop && (
          <div
            role="presentation"
            onClick={onMobileClose}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          />
        )}
        <div
          ref={ref}
          className={cn(
            "flex h-full flex-col bg-gradient-to-b from-primary to-primary-dark text-white transition-all duration-180 ease-out",
            "fixed inset-y-0 left-0 z-50 -translate-x-full lg:static lg:translate-x-0",
            mobileOpen && "translate-x-0",
            className,
          )}
          style={{ width: isCollapsed ? "70px" : "260px" }}
          {...props}
        >
          <div className={cn("flex items-center border-b border-white/10 px-4", isCollapsed ? "h-16 justify-center" : "h-16")}>
            <Logo variant={isCollapsed ? "icon" : "compact"} className="text-white" />
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="flex flex-col gap-1 px-2">
              {menuItems.map((item) => renderItem(item))}
            </ul>
          </nav>
          {footerUser && !isCollapsed && (
            <div className="border-t border-white/10 px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-xs font-bold text-white">
                  {getInitials(footerUser.name)}
                </div>
                <div className="flex flex-col overflow-hidden">
                  <span className="truncate text-sm font-medium text-white">{footerUser.name}</span>
                  <span className="truncate text-xs text-white/60">{footerUser.email}</span>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={handleToggle}
            aria-label="Alternar barra lateral"
            data-i18n-attr='{"aria-label":"common.toggleSidebar"}'
            className="flex items-center justify-center border-t border-white/10 py-3 text-white/50 hover:text-white transition-colors duration-150"
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </>
    );
  },
);
Sidebar.displayName = "Sidebar";

export { Sidebar, getInitials };
